/**
 * ResearchPanel — competitor research for one persona.
 *
 * Operator pastes competitor @handles → Ghost scrapes each profile (with the
 * persona's linked IG account, sequentially and gently) → Claude writes a
 * research brief that the script prompt then reads. This panel is the whole
 * loop: add handles, run, read the brief, inspect per-competitor numbers.
 */

import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ExternalLink, Play, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/input';
import { MarkdownMessage } from '@/components/chat/MarkdownMessage';
import {
  normalizeHandle,
  RESEARCH_MAX_HANDLES,
  RESEARCH_POLL_MS,
  RESEARCH_TOP_POSTS,
} from '@/constants/research';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import type { Competitor, CompetitorPost } from '@/types/research';

function fmtCount(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function topPosts(c: Competitor): CompetitorPost[] {
  const posts = c.latest_snapshot?.recent_posts ?? [];
  return [...posts]
    .sort((a, b) => (b.views ?? b.likes ?? 0) - (a.views ?? a.likes ?? 0))
    .slice(0, RESEARCH_TOP_POSTS);
}

export function ResearchPanel({ personaId }: { personaId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');

  const overviewQ = useQuery({
    queryKey: ['persona', personaId, 'research'],
    queryFn: () => api.getResearch(personaId),
    refetchInterval: RESEARCH_POLL_MS,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['persona', personaId, 'research'] });

  const addM = useMutation({
    mutationFn: (handles: string[]) => api.addCompetitors(personaId, handles),
    onSuccess: (rows) => {
      toast.success(`Tracking ${rows.length} competitor${rows.length === 1 ? '' : 's'}.`);
      setDraft('');
      void invalidate();
    },
    onError: (err) => toast.error('Could not add handles', { description: String(err) }),
  });

  const removeM = useMutation({
    mutationFn: (competitorId: string) => api.removeCompetitor(personaId, competitorId),
    onSuccess: () => void invalidate(),
    onError: (err) => toast.error('Remove failed', { description: String(err) }),
  });

  const runM = useMutation({
    mutationFn: () => api.triggerResearch(personaId),
    onSuccess: (res) => {
      toast.success('Research started; watching the run.', {
        description: 'Scrapes run one at a time with pauses to protect the account.',
      });
      void navigate({ to: '/runs/$workflowId', params: { workflowId: res.workflow_id } });
    },
    onError: (err) => toast.error('Research failed to start', { description: String(err) }),
  });

  const parsed = draft
    .split(/[\s,]+/)
    .map((h) => h.trim())
    .filter(Boolean);
  const valid = Array.from(
    new Set(parsed.map(normalizeHandle).filter((h): h is string => h !== null)),
  );
  const invalid = parsed.filter((h) => normalizeHandle(h) === null);
  const tooMany = valid.length > RESEARCH_MAX_HANDLES;

  const overview = overviewQ.data;
  const competitors = overview?.competitors ?? [];
  const hasAccount = !!overview?.scrape_account_id;
  const budgetLeft =
    overview && hasAccount ? Math.max(0, overview.daily_cap - overview.scrapes_last_24h) : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Competitors</CardTitle>
          <CardDescription>
            Paste Instagram handles (one per line, commas, or with @). Ghost reads each public
            profile with this persona's linked account, then Claude writes a research brief that
            the script prompt uses for hooks and formats — never for copying.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-start">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={'@fitcoach.jo\nhome.workouts.daily\nhttps://www.instagram.com/liftlab/'}
              rows={3}
              className="md:flex-1"
            />
            <Button
              type="button"
              onClick={() => addM.mutate(valid)}
              disabled={addM.isPending || valid.length === 0 || tooMany}
              className="shrink-0"
            >
              <Plus className="h-4 w-4" />
              {addM.isPending ? 'Adding…' : `Add ${valid.length || ''}`.trim()}
            </Button>
          </div>
          {invalid.length > 0 && (
            <p className="text-xs text-destructive">
              Not valid handles: {invalid.slice(0, 5).join(', ')}
              {invalid.length > 5 ? '…' : ''}
            </p>
          )}
          {tooMany && (
            <p className="text-xs text-destructive">
              Max {RESEARCH_MAX_HANDLES} handles per add.
            </p>
          )}

          {overviewQ.isLoading ? (
            <Skeleton className="h-16" />
          ) : competitors.length === 0 ? (
            <p className="text-sm text-muted-foreground">No competitors tracked yet.</p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {competitors.map((c) => {
                const snap = c.latest_snapshot;
                return (
                  <li key={c.competitor_id} className="space-y-1.5 px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <a
                        href={`https://www.instagram.com/${c.handle}/`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 font-mono text-foreground hover:underline"
                      >
                        @{c.handle}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      {snap ? (
                        <>
                          <Badge tone="neutral">{fmtCount(snap.followers)} followers</Badge>
                          <Badge tone="neutral">{fmtCount(snap.post_count)} posts</Badge>
                          {snap.is_private && <Badge tone="warning">private</Badge>}
                          <span className="text-xs text-muted-foreground">
                            scraped {snap.scraped_at ? formatRelative(snap.scraped_at) : '—'}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">not scraped yet</span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ml-auto"
                        title="Stop tracking"
                        onClick={() => removeM.mutate(c.competitor_id)}
                        disabled={removeM.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {snap && topPosts(c).length > 0 && (
                      <ul className="flex flex-wrap gap-1.5">
                        {topPosts(c).map((p) => (
                          <li key={p.shortcode ?? p.url ?? Math.random()}>
                            <a
                              href={p.url ?? '#'}
                              target="_blank"
                              rel="noreferrer noopener"
                              title={p.caption ?? ''}
                              className="inline-flex max-w-[16rem] items-center gap-1 truncate rounded border border-border bg-muted/40 px-2 py-0.5 text-xs hover:bg-muted"
                            >
                              <span className="font-mono">
                                {p.views != null ? `${fmtCount(p.views)} views` : `${fmtCount(p.likes)} likes`}
                              </span>
                              <span className="truncate text-muted-foreground">
                                {(p.caption ?? '').split('\n')[0]}
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">
              {overview && !hasAccount ? (
                <span className="text-amber-700 dark:text-amber-300">
                  Link an Instagram account with credentials to this persona first — scrapes run
                  through it.
                </span>
              ) : budgetLeft === null ? (
                'Scrapes use this persona’s linked Instagram account.'
              ) : (
                `${budgetLeft} of ${overview?.daily_cap} scrapes left today on the linked account.`
              )}
              {competitors.length > 0 && budgetLeft !== null && budgetLeft < competitors.length && (
                <span className="ml-1 text-amber-700 dark:text-amber-300">
                  Not enough budget for all {competitors.length} handles — remove some or wait.
                </span>
              )}
            </p>
            <Button
              type="button"
              onClick={() => runM.mutate()}
              disabled={runM.isPending || competitors.length === 0 || !hasAccount}
            >
              <Play className="h-4 w-4" />
              {runM.isPending ? 'Starting…' : 'Run research'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            Research brief
            {overview?.brief?.created_at && (
              <span className="text-xs font-normal text-muted-foreground">
                {formatRelative(overview.brief.created_at)}
                {overview.brief.model ? ` · ${overview.brief.model}` : ''}
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Fed into every script prompt for this persona as "use for formats and hooks, do not
            copy". Re-run research to refresh it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {overviewQ.isLoading ? (
            <Skeleton className="h-32" />
          ) : overview?.brief ? (
            <MarkdownMessage text={overview.brief.brief_md} />
          ) : (
            <p className="text-sm text-muted-foreground">
              No brief yet. Add competitors and run research. Runs show up on the{' '}
              <Link to="/runs" className="underline">
                Runs
              </Link>{' '}
              page.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
